import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db, type MutationEntry, type MutationStatus } from './db';
import type { FormDraft } from './formDrafts';
import { nextAttemptAt } from './retryBackoff';

/**
 * Offline reliability §2 — "unsent work is visible everywhere".
 *
 * ONE shape for the two places work waits on a device: the mutation outbox
 * (a save the server has not acknowledged) and the device drafts (typing the
 * person has not yet saved anywhere). Today, the Forms hub, the nav badge and
 * the Sync Center all read this list, so a log queued on Tuesday under a
 * different job cannot hide behind the page it was written on.
 */
export type UnsentSource = 'outbox' | 'draft';

export type UnsentStatus = Exclude<MutationStatus, 'completed'> | 'draft';

export interface UnsentItem {
  /** Stable, unique across both sources: `outbox:<row id>` or `draft:<key>`. */
  id: string;
  source: UnsentSource;
  /** Outbox: the replay kind (entityType). Draft: the key's prefix (daily-log, form-fill, …). */
  kind: string;
  title: string;
  subtitle: string;
  status: UnsentStatus;
  attempts?: number;
  lastError?: string;
  nextRetryAt?: Date | null;
  photoCount?: number;
  /** When the work was captured (outbox: createdAt; draft: updatedAt). */
  savedAt: Date;
  /** Where "Open" goes — the page that can finish or resend it. */
  href: string;
  /** Outbox row id, for Retry / Discard. */
  outboxId?: number;
  /** Draft key, for Open / Discard. */
  draftKey?: string;
  /** Approximate bytes on the device (drafts only — values plus staged blobs). */
  bytes?: number;
  project?: string | null;
  date?: string | null;
}

/** The outbox statuses that mean "not on the server yet". */
export const UNSENT_STATUSES: MutationStatus[] = ['pending', 'syncing', 'failed', 'conflict'];

/** Plain words for each replay kind — what a foreman calls the thing, not the code. */
export const KIND_LABELS: Record<string, string> = {
  'dailyLog.upsert': 'Daily log',
  'dailyLog.full': 'Daily log with photos',
  'formSubmission.full': 'Form',
  'incident.report': 'Incident report',
  'incident.report.full': 'Incident report',
  'safety.observation.full': 'Safety observation',
  'safety.inspection.submit': 'Safety inspection',
  'safety.stopWork.create': 'Stop work',
  'equipment.inspection': 'Equipment inspection',
  'equipment.inspection.full': 'Equipment inspection',
  'equipmentReading.create': 'Equipment reading',
  'equipmentUsage.replace': 'Equipment usage',
  'workforce.assign': 'Crew assignment',
  'purchaseRequest.draft': 'Purchase request',
  'purchaseRequest.draftUpdate': 'Purchase request',
  'scrapTicket.create': 'Scrap ticket',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

type Rec = Record<string, unknown>;
const isRec = (v: unknown): v is Rec => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : typeof v === 'number' ? String(v) : null);

/**
 * Pull the project and date out of any of the outbox payload shapes. Every
 * kind wraps its record differently (`upsert`, `submission`, `incident`, …),
 * so this looks in the known wrappers and then at the root, and takes the
 * first name-like field it finds. Tolerant by design: a payload it does not
 * recognise yields nulls, never a throw.
 */
export function describePayload(payload: unknown): { project: string | null; date: string | null; photoCount: number } {
  if (!isRec(payload)) return { project: null, date: null, photoCount: 0 };
  const wrappers = ['upsert', 'submission', 'incident', 'observation', 'inspection', 'request', 'event', 'reading', 'ticket'];
  const candidates: Rec[] = [];
  for (const w of wrappers) if (isRec(payload[w])) candidates.push(payload[w] as Rec);
  candidates.push(payload);

  let project: string | null = null;
  let date: string | null = null;
  for (const c of candidates) {
    project ??= str(c.project_name) ?? str(c.project) ?? str(c.project_number) ?? str(c.project_id);
    date ??= str(c.log_date) ?? str(c.date) ?? str(c.incident_date) ?? str(c.inspection_date) ?? str(c.observed_at) ?? str(c.reading_date);
  }
  const attachments = payload.attachments;
  const photoCount = Array.isArray(attachments) ? attachments.length : 0;
  return { project, date, photoCount };
}

/** Deep-link anchors: `/sync#outbox-<row id>` or `/sync#draft-<key>` land on that Sync Center row. */
export const outboxAnchor = (id: number) => `outbox-${id}`;
export const draftAnchor = (key: string) => `draft-${encodeURIComponent(key)}`;

/** An outbox row's home is its Sync Center row — that is where Retry / Discard live. */
function outboxHref(m: MutationEntry): string {
  return m.id !== undefined ? `/sync#${outboxAnchor(m.id)}` : '/sync';
}

export function outboxToUnsent(m: MutationEntry): UnsentItem {
  const { project, date, photoCount } = describePayload(m.payload);
  const title = m.label || kindLabel(m.entityType);
  const bits = [kindLabel(m.entityType), project, date].filter(Boolean) as string[];
  const status = (m.status === 'completed' ? 'pending' : m.status) as UnsentStatus;
  return {
    id: `outbox:${m.id}`,
    source: 'outbox',
    kind: m.entityType,
    title,
    subtitle: bits.join(' · '),
    status,
    attempts: m.attempts,
    lastError: m.error,
    nextRetryAt: m.status === 'pending' ? nextAttemptAt(m) : null,
    photoCount,
    savedAt: m.createdAt instanceof Date ? m.createdAt : new Date(m.createdAt),
    href: outboxHref(m),
    outboxId: m.id,
    project,
    date,
  };
}

/* ------------------------------------------------------------------ drafts */

/**
 * Is this device draft work the server does not have?
 *
 * A draft carries the identity it was written against: `new` when nothing
 * existed on the server, else the record id plus the version (updated_at)
 * that was loaded. Written against `new` → unsynced. Written against a saved
 * record and stamped AFTER that record's version → newer than the server →
 * unsynced. Only a draft that is provably no newer than the copy it loaded
 * counts as already saved. A draft with no identity at all predates the
 * identity stamp; nothing proves it was saved, so it is kept as unsent.
 */
export function isDraftUnsynced(d: Pick<FormDraft, 'updatedAt' | 'identity'>): boolean {
  const idn = d.identity;
  if (!idn) return true;
  if (idn.logId === 'new') return true;
  if (idn.version == null || idn.version === '') return true;
  const server = new Date(String(idn.version)).getTime();
  const saved = new Date(d.updatedAt).getTime();
  if (Number.isNaN(server) || Number.isNaN(saved)) return true;
  return saved > server;
}

/** Bytes a draft holds: its values as JSON plus every staged Blob. */
export function approximateDraftBytes(d: Pick<FormDraft, 'values' | 'signerNames'>): number {
  let blobs = 0;
  const seen = new WeakSet<object>();
  const walk = (v: unknown): unknown => {
    if (v instanceof Blob) { blobs += v.size; return undefined; }
    if (Array.isArray(v)) return v.map(walk);
    if (isRec(v)) {
      if (seen.has(v)) return undefined;
      seen.add(v);
      const out: Rec = {};
      for (const [k, x] of Object.entries(v)) out[k] = walk(x);
      return out;
    }
    return v;
  };
  let json = 0;
  try { json = JSON.stringify({ values: walk(d.values), signerNames: d.signerNames })?.length ?? 0; } catch { json = 0; }
  return json + blobs;
}

export function draftToUnsent(d: FormDraft): UnsentItem {
  const [prefix, ...rest] = d.key.split(':');
  const savedAt = new Date(d.updatedAt);
  const logId = d.identity?.logId;
  let title = 'Draft';
  let subtitle = 'On this device';
  let href = '/sync';
  let project: string | null = null;
  let date: string | null = null;
  if (prefix === 'daily-log') {
    const [pid, logDate] = rest;
    project = pid ?? null;
    date = logDate ?? null;
    title = 'Daily log';
    subtitle = [logDate, 'draft on this device'].filter(Boolean).join(' · ');
    href = logId && logId !== 'new' ? `/daily-logs/${logId}` : `/daily-logs/new?project_id=${pid}&date=${logDate}`;
  } else if (prefix === 'form-fill') {
    title = 'Form';
    subtitle = 'draft on this device';
    href = `/forms/${rest.join(':')}/fill`;
  } else if (prefix === 'condition-report') {
    const [eid, phase] = rest;
    title = 'Condition report';
    subtitle = [phase, 'draft on this device'].filter(Boolean).join(' · ');
    href = `/equipment/${eid}`;
  }
  return {
    id: `draft:${d.key}`,
    source: 'draft',
    kind: prefix,
    title,
    subtitle,
    status: 'draft',
    savedAt: Number.isNaN(savedAt.getTime()) ? new Date(0) : savedAt,
    href,
    draftKey: d.key,
    bytes: approximateDraftBytes(d),
    project,
    date,
  };
}

/* ------------------------------------------------------------------- list */

/** Every outbox row the server has not acknowledged. */
export async function listUnsentOutbox(): Promise<MutationEntry[]> {
  return db.mutations.where('status').anyOf(UNSENT_STATUSES).toArray();
}

/** Every device draft that is `new` or newer than the server copy it loaded. */
export async function listUnsyncedDrafts(): Promise<FormDraft[]> {
  const all = await db.formDrafts.toArray();
  return all.filter(isDraftUnsynced);
}

/**
 * Both sources, one shape, oldest first — the oldest is the one to worry about.
 * Rows saved in the same millisecond keep the order the outbox queued them in
 * (the status index would otherwise put `failed` before `pending`); drafts
 * follow outbox rows on a tie.
 */
export async function listUnsent(): Promise<UnsentItem[]> {
  const [rows, drafts] = await Promise.all([listUnsentOutbox(), listUnsyncedDrafts()]);
  const items = [...rows.map(outboxToUnsent), ...drafts.map(draftToUnsent)];
  const queued = (i: UnsentItem) => i.outboxId ?? Number.MAX_SAFE_INTEGER;
  return items.sort((a, b) => a.savedAt.getTime() - b.savedAt.getTime() || queued(a) - queued(b));
}

/** The nav badge's number: outbox rows not done plus unsynced drafts. */
export async function countUnsent(): Promise<number> {
  const [rows, drafts] = await Promise.all([
    db.mutations.where('status').anyOf(UNSENT_STATUSES).count(),
    listUnsyncedDrafts(),
  ]);
  return rows + drafts.length;
}

/** Oldest item's capture time, or null when nothing waits. */
export function oldestUnsent(items: UnsentItem[]): Date | null {
  if (items.length === 0) return null;
  return items.reduce((min, i) => (i.savedAt < min ? i.savedAt : min), items[0].savedAt);
}

/**
 * Every failed or backed-off outbox row goes straight back to the front of
 * the queue. Conflicts are NOT touched — "keep mine" is a per-item choice
 * (it rewrites the payload with force: true). Returns how many were reset.
 */
export async function requeueAllUnsent(): Promise<number> {
  return db.mutations.where('status').anyOf(['failed', 'pending'])
    .modify({ status: 'pending', attempts: 0, error: undefined, lastAttempt: undefined });
}

/* ------------------------------------------------------------------- hook */

/** Poll period while mounted — a belt for anything liveQuery does not observe (the backoff clock). */
export const UNSENT_POLL_MS = 10_000;

/**
 * Live list of unsent work. Dexie's liveQuery re-runs the read whenever the
 * mutations or formDrafts tables change; the ten-second tick re-derives the
 * wait times so "retrying in about a minute" does not go stale.
 */
export function useUnsent(): UnsentItem[] | undefined {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), UNSENT_POLL_MS);
    return () => clearInterval(t);
  }, []);
  return useLiveQuery(() => listUnsent(), [tick]);
}

/** Live count for the nav badge. */
export function useUnsentCount(): number {
  return useLiveQuery(() => countUnsent(), [], 0) ?? 0;
}
